import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'overview',  label: 'Overview',     icon: 'fa-chart-line' },
  { id: 'users',     label: 'Signups',      icon: 'fa-users' },
  { id: 'questions', label: 'VIVI Q&A',     icon: 'fa-comment-dots' },
  { id: 'reads',     label: 'Article Reads',icon: 'fa-book-open' },
  { id: 'clicks',    label: 'Click Stats',  icon: 'fa-hand-pointer' },
  { id: 'bookings',  label: 'Bookings',     icon: 'fa-calendar' },
];

function Stat({ label, value, sub, icon }) {
  return (
    <div className="immersive-card" style={{ padding: 18, background: 'var(--bg2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <i className={`fa-solid ${icon}`} style={{ color: 'var(--gold)', fontSize: '1.1rem' }} />
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.05rem', color: 'var(--gold)', lineHeight: 1, textShadow: '0 0 20px rgba(212,175,55,0.3)' }}>{value ?? '—'}</div>
      </div>
      <div style={{ fontSize: '0.79rem', color: 'var(--text)', fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.71rem', color: 'var(--text-dim)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user, authHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reads, setReads] = useState([]);
  const [readsTop, setReadsTop] = useState([]);
  const [readsSubTab, setReadsSubTab] = useState('top');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/home'); return; }
    loadOverview();
  }, []);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/overview`, { headers: authHeader() });
      setOverview(r.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  const switchTab = async (next) => {
    setTab(next);
    setLoading(true);
    try {
      if (next === 'users')     setUsers((await axios.get(`${API}/admin/users`, { headers: authHeader() })).data.users || []);
      if (next === 'questions') setQuestions((await axios.get(`${API}/admin/vivi-questions`, { headers: authHeader() })).data.questions || []);
      if (next === 'clicks')    setClicks((await axios.get(`${API}/admin/clicks`, { headers: authHeader() })).data.clicks || []);
      if (next === 'bookings')  setBookings((await axios.get(`${API}/admin/bookings`, { headers: authHeader() })).data.bookings || []);
      if (next === 'reads') {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/admin/article-reads/top`, { headers: authHeader() }),
          axios.get(`${API}/admin/article-reads?limit=300`, { headers: authHeader() }),
        ]);
        setReadsTop(r1.data.top || []);
        setReads(r2.data.reads || []);
      }
      if (next === 'overview')  await loadOverview();
    } catch { /* */ } finally { setLoading(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 40 }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 16px 14px', borderBottom: '1px solid var(--border-soft)',
      }}>
        <div>
          <div className="eyebrow">INTIVIV™ · Admin</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.65rem' }}>
            Welcome, <span className="gold-italic">Dr. Vikram</span>
          </div>
        </div>
        <button
          onClick={() => { logout(); navigate('/auth'); }}
          className="btn-ghost"
          style={{ fontSize: '0.75rem', letterSpacing: '0.18em' }}
          data-testid="admin-logout"
        >
          <i className="fa-solid fa-right-from-bracket" /> LOGOUT
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, overflowX: 'auto', borderBottom: '1px solid var(--border-soft)' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            style={{
              flex: '0 0 auto',
              padding: '14px 18px',
              background: 'none',
              border: 'none',
              borderBottom: '2px solid ' + (tab === t.id ? 'var(--gold)' : 'transparent'),
              color: tab === t.id ? 'var(--gold)' : 'var(--text-dim)',
              fontSize: '0.79rem', letterSpacing: '0.12em', fontWeight: 700, textTransform: 'uppercase',
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <i className={`fa-solid ${t.icon}`} style={{ marginRight: 6 }} />{t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 16 }}>
        {loading && (
          <div style={{ padding: 30, textAlign: 'center' }}>
            <div className="gold-loader" style={{ margin: '0 auto' }} />
          </div>
        )}

        {/* OVERVIEW */}
        {tab === 'overview' && overview && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <Stat icon="fa-users"           label="Total Signups"        value={overview.total_users}        sub={`+${overview.last_24h_signups} in last 24h`} />
            <Stat icon="fa-comment-dots"    label="VIVI Questions"       value={overview.total_vivi_questions} />
            <Stat icon="fa-clipboard-check" label="Assessments Taken"    value={overview.total_assessments} />
            <Stat icon="fa-calendar-check"  label="Bookings"             value={overview.total_bookings} />
            <Stat icon="fa-hand-pointer"    label="Tracked Clicks"       value={overview.total_clicks} />
            <Stat icon="fa-shield-halved"   label="System"               value="OK" sub="All services healthy" />
          </div>
        )}

        {/* SIGNUPS */}
        {tab === 'users' && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>{users.length} signups</div>
            {users.map(u => (
              <div key={u.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 14px', background: 'var(--bg2)', border: '1px solid var(--border-soft)',
                marginBottom: 6,
              }} data-testid={`admin-user-row-${u.id}`}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.93rem', fontWeight: 600, color: 'var(--text)' }}>{u.name || '—'}</div>
                  <div style={{ fontSize: '0.79rem', color: 'var(--text-muted)' }}>{u.email}</div>
                </div>
                <div style={{ fontSize: '0.73rem', color: 'var(--text-dim)', textAlign: 'right' }}>
                  {fmt(u.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* VIVI QUESTIONS */}
        {tab === 'questions' && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>{questions.length} most recent VIVI questions</div>
            {questions.map((q, i) => (
              <div key={i} style={{
                padding: 14, background: 'var(--bg2)', border: '1px solid var(--border-soft)',
                marginBottom: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.76rem', color: 'var(--text-dim)' }}>
                  <span><i className="fa-solid fa-user" style={{ marginRight: 5, color: 'var(--gold)' }} />{q.email}</span>
                  <span>{fmt(q.timestamp)}</span>
                </div>
                <div style={{ fontSize: '0.93rem', color: 'var(--text)', lineHeight: 1.55 }}>"{q.question}"</div>
              </div>
            ))}
          </div>
        )}

        {/* CLICK STATS */}
        {tab === 'clicks' && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Click counts by event · {clicks.length} unique events</div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border-soft)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', padding: '10px 14px', borderBottom: '1px solid var(--border-soft)', fontSize: '0.73rem', color: 'var(--text-dim)', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                <span>Event</span><span>Label</span><span style={{ textAlign: 'right' }}>Count</span>
              </div>
              {clicks.map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', padding: '10px 14px', borderBottom: '1px solid var(--border-soft)', fontSize: '0.87rem' }}>
                  <span style={{ color: 'var(--gold)' }}>{c.event}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{c.label || '—'}</span>
                  <span style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 600 }}>{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ARTICLE READS */}
        {tab === 'reads' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[
                { id: 'top',  label: `Top Articles · ${readsTop.length}` },
                { id: 'feed', label: `Live Feed · ${reads.length}` },
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => setReadsSubTab(s.id)}
                  data-testid={`reads-subtab-${s.id}`}
                  style={{
                    padding: '6px 14px',
                    background: readsSubTab === s.id ? 'rgba(212,175,55,0.14)' : 'transparent',
                    border: '1px solid ' + (readsSubTab === s.id ? 'var(--gold)' : 'var(--border-soft)'),
                    color: readsSubTab === s.id ? 'var(--gold)' : 'var(--text-muted)',
                    fontSize: '0.76rem', letterSpacing: '0.14em', fontWeight: 700, textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >{s.label}</button>
              ))}
            </div>

            {readsSubTab === 'top' && (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border-soft)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 110px', padding: '10px 14px', borderBottom: '1px solid var(--border-soft)', fontSize: '0.73rem', color: 'var(--text-dim)', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                  <span>Article</span><span style={{ textAlign: 'right' }}>Views</span><span style={{ textAlign: 'right' }}>Readers</span><span style={{ textAlign: 'right' }}>Last</span>
                </div>
                {readsTop.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.87rem' }}>No article reads yet.</div>}
                {readsTop.map((r, i) => (
                  <div key={r.article_id || i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 110px', padding: '12px 14px', borderBottom: '1px solid var(--border-soft)', fontSize: '0.87rem', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || r.article_id}</div>
                      <div style={{ fontSize: '0.71rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 3 }}>{r.category || '—'}</div>
                    </div>
                    <span style={{ textAlign: 'right', color: 'var(--gold)', fontWeight: 700 }}>{r.total_views}</span>
                    <span style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{r.unique_readers}</span>
                    <span style={{ textAlign: 'right', color: 'var(--text-dim)', fontSize: '0.71rem' }}>{fmt(r.last_read_at)}</span>
                  </div>
                ))}
              </div>
            )}

            {readsSubTab === 'feed' && (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border-soft)' }}>
                {reads.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.87rem' }}>No reads logged yet.</div>}
                {reads.map((r) => (
                  <div key={r.id} style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-soft)' }} data-testid={`admin-read-row-${r.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '0.87rem', color: 'var(--text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.article_title || r.article_id}</div>
                        <div style={{ fontSize: '0.76rem', color: 'var(--gold)', marginTop: 3 }}>
                          <i className="fa-solid fa-user" style={{ marginRight: 5 }} />
                          {r.user_name || '—'} · <span style={{ color: 'var(--text-muted)' }}>{r.user_email}</span>
                        </div>
                        <div style={{ fontSize: '0.69rem', color: 'var(--text-dim)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{r.article_category || '—'}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '0.73rem', color: 'var(--text-dim)' }}>{fmt(r.last_read_at)}</div>
                        <div style={{ fontSize: '0.69rem', color: 'var(--gold)', marginTop: 3 }}>{r.views_today} view{r.views_today === 1 ? '' : 's'} today</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BOOKINGS */}
        {tab === 'bookings' && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>{bookings.length} bookings</div>
            {bookings.map(b => (
              <div key={b.id} style={{ padding: 14, background: 'var(--bg2)', border: '1px solid var(--border-soft)', marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '0.94rem', fontWeight: 600 }}>{b.name || b.email}</div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{b.email} · {b.phone}</div>
                  </div>
                  <div style={{ fontSize: '0.71rem', color: 'var(--gold)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{b.status}</div>
                </div>
                {b.concern && <div style={{ fontSize: '0.87rem', marginTop: 8, color: 'var(--text)' }}>"{b.concern}"</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
